"use client";
import React, { useEffect, useRef } from "react";
import VideoPlayer from "./videoPlayer/VideoPlayer";
import { gsap, ScrollTrigger } from "@/lib/gsap";
import HeroButtons from "./HeroButtons";

import { HeroClientProps } from "@/types/components";
import { throttleRAF, getWindowDimensions } from "@/lib/helper";

const HeroClient = ({ data }: HeroClientProps) => {
  const heroContainerRef = useRef<HTMLElement>(null);
  const deviceMockupRef = useRef<HTMLDivElement>(null);
  const videoInnerRef = useRef<HTMLDivElement>(null);
  const heroTextRef = useRef<HTMLDivElement>(null);
  const getResponsiveInitialY = () => {
    if (typeof window === "undefined") return 60;
    const screenWidth = window.innerWidth;
    if (screenWidth < 768) {
      return 0;
    } else {
      return 60;
    }
  };

  const getResponsiveInitialScale = () => {
    if (typeof window === "undefined") return 0.94;
    const { width } = getWindowDimensions();
    if (width < 768) {
      return 1;
    } else {
      return 0.94;
    }
  };
  useEffect(() => {
    const deviceMockup = deviceMockupRef.current;
    const heroText = heroTextRef.current;
    const heroContainer = heroContainerRef.current;

    if (!deviceMockup || !heroText || !heroContainer) return;

    const { width } = getWindowDimensions();
    const isMobile = width < 768;

    if (isMobile) {
      return;
    }

    const ctx = gsap.context(() => {
      gsap.set(deviceMockup, {
        scale: 1,
      });

      const getResponsiveScrollEnd = () => {
        const { width } = getWindowDimensions();
        if (width < 1024) {
          return "+=1200";
        } else {
          return "+=1500";
        }
      };

      const heroTimeline = gsap.timeline({
        scrollTrigger: {
          trigger: heroContainer,
          start: "top top",
          end: getResponsiveScrollEnd(),
          scrub: 1.2,
          pin: true,
          anticipatePin: 1,
          invalidateOnRefresh: true,
        },
      });

      const getResponsiveY = () => {
        const { width } = getWindowDimensions();
        if (width < 1024) {
          return "-93%";
        } else {
          return "-120%";
        }
      };

      heroTimeline.to(
        heroText,
        {
          opacity: 0,
          y: getResponsiveY(),
          height: 0,
          paddingTop: 0,
          paddingBottom: 0,
          marginTop: 0,
          marginBottom: 0,
          duration: 0.5,
        },
        0
      );

      gsap.set(deviceMockup, {
        transformOrigin: "center center",
      });

      heroTimeline.to(
        deviceMockup,
        {
          scale: 1.05,
          y: -30,
          duration: 1,
          ease: "power2.inOut",
        },
        0
      );

      heroTimeline.to({}, { duration: 0.5 });
    }, heroContainer);

    const handleResize = throttleRAF(() => {
      getWindowDimensions(true);
      requestAnimationFrame(() => {
        ScrollTrigger.refresh();
      });
    }, 250);

    if (typeof window !== "undefined") {
      window.addEventListener("resize", handleResize);
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", handleResize);
      }
      ctx.revert();
    };
  }, []);

  const headingRef = useRef<HTMLDivElement>(null);
  const subtitleRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (
      !heroContainerRef.current ||
      !headingRef.current ||
      !subtitleRef.current ||
      !ctaRef.current ||
      !deviceMockupRef.current
    )
      return;

    const ctx = gsap.context(() => {
      const { width } = getWindowDimensions();
      const isMobile = width < 768;
      const initialScale = isMobile ? 1 : getResponsiveInitialScale();

      gsap.set(deviceMockupRef.current, {
        y: getResponsiveInitialY(),
        scale: initialScale,
      });

      // Set initial state to prevent layout shift - use transform instead of y for better performance
      // This prevents reflow and ensures space is properly reserved
      gsap.set([headingRef.current, subtitleRef.current, ctaRef.current], {
        opacity: 0,
        transform: "translateY(40px)", // Use transform instead of y to prevent layout shift
        visibility: "visible", // Keep visible to reserve space
        willChange: "transform, opacity", // Hint browser for optimization
      });

      const timeline = gsap.timeline({
        delay: 0.2,
      });

      timeline
        .to(headingRef.current, {
          opacity: 1,
          transform: "translateY(0)", // Use transform instead of y
          duration: 0.8,
          ease: "power2.out",
          clearProps: "transform",
        })
        .to(
          subtitleRef.current,
          {
            opacity: 1,
            transform: "translateY(0)", // Use transform instead of y
            duration: 0.8,
            ease: "power2.out",
            clearProps: "transform",
          },
          "-=0.6"
        )
        .to(
          ctaRef.current,
          {
            opacity: 1,
            transform: "translateY(0)", // Use transform instead of y
            duration: 0.8,
            ease: "power2.out",
            clearProps: "transform",
          },
          "-=0.6"
        );

      if (isMobile) {
        gsap.to(deviceMockupRef.current, {
          opacity: 1,
          scale: 1,
          duration: 1,
          delay: 0.5,
          ease: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
        });
      } else {
        gsap.to(deviceMockupRef.current, {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 1,
          delay: 0.5,
          ease: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
        });
      }
    }, heroContainerRef);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={heroContainerRef} id="hero-container" className="relative">
      <div className="relative w-full  max-sm:min-h-[88vh] sm:min-h-screen lg:rounded-b-[32px] sm:h-screen flex flex-col justify-start items-center text-center overflow-hidden bg-theme-secondary isolation-isolate sm:justify-evenly sm:pt-8 sm:pb-8">
        <div className="absolute inset-0 w-full h-full z-0">
          <div className="block md:hidden absolute inset-0 w-full h-full translate-y-[70px]">
            <svg
              width="391"
              height="497"
              viewBox="0 0 391 497"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="w-full h-full object-cover"
            >
              <g opacity="0.7" filter="url(#filter0_f_1044_1200)">
                <rect
                  x="323.016"
                  y="471.63"
                  width="345.241"
                  height="294.791"
                  transform="rotate(-147.086 323.016 471.63)"
                  fill="url(#paint0_radial_1044_1200)"
                />
              </g>
              <g opacity="0.64" filter="url(#filter1_f_1044_1200)">
                <rect
                  x="81.1841"
                  y="436.122"
                  width="265.619"
                  height="226.716"
                  transform="rotate(-147.086 81.1841 436.122)"
                  fill="url(#paint1_radial_1044_1200)"
                />
              </g>
              <g opacity="0.87" filter="url(#filter2_f_1044_1200)">
                <rect
                  x="247.246"
                  y="563.891"
                  width="295.397"
                  height="296.612"
                  transform="rotate(-152.879 247.246 563.891)"
                  fill="url(#paint2_radial_1044_1200)"
                />
              </g>
              <defs>
                <filter
                  id="filter0_f_1044_1200"
                  x="-3.21978"
                  y="0.149235"
                  width="522.83"
                  height="507.892"
                  filterUnits="userSpaceOnUse"
                  colorInterpolationFilters="sRGB"
                >
                  <feFlood floodOpacity="0" result="BackgroundImageFix" />
                  <feBlend
                    mode="normal"
                    in="SourceGraphic"
                    in2="BackgroundImageFix"
                    result="shape"
                  />
                  <feGaussianBlur
                    stdDeviation="18.2054"
                    result="effect1_foregroundBlur_1044_1200"
                  />
                </filter>
                <filter
                  id="filter1_f_1044_1200"
                  x="-169.867"
                  y="73.3974"
                  width="402.31"
                  height="390.791"
                  filterUnits="userSpaceOnUse"
                  colorInterpolationFilters="sRGB"
                >
                  <feFlood floodOpacity="0" result="BackgroundImageFix" />
                  <feBlend
                    mode="normal"
                    in="SourceGraphic"
                    in2="BackgroundImageFix"
                    result="shape"
                  />
                  <feGaussianBlur
                    stdDeviation="14.0333"
                    result="effect1_foregroundBlur_1044_1200"
                  />
                </filter>
                <filter
                  id="filter2_f_1044_1200"
                  x="-52.082"
                  y="128.818"
                  width="470.955"
                  height="471.483"
                  filterUnits="userSpaceOnUse"
                  colorInterpolationFilters="sRGB"
                >
                  <feFlood floodOpacity="0" result="BackgroundImageFix" />
                  <feBlend
                    mode="normal"
                    in="SourceGraphic"
                    in2="BackgroundImageFix"
                    result="shape"
                  />
                  <feGaussianBlur
                    stdDeviation="18.2054"
                    result="effect1_foregroundBlur_1044_1200"
                  />
                </filter>
                <radialGradient
                  id="paint0_radial_1044_1200"
                  cx="0"
                  cy="0"
                  r="1"
                  gradientUnits="userSpaceOnUse"
                  gradientTransform="translate(495.637 619.026) scale(172.621 147.092)"
                >
                  <stop stopColor="#AEC2E7" />
                  <stop offset="1" stopColor="#AEC2E7" stopOpacity="0" />
                </radialGradient>
                <radialGradient
                  id="paint1_radial_1044_1200"
                  cx="0"
                  cy="0"
                  r="1"
                  gradientUnits="userSpaceOnUse"
                  gradientTransform="translate(213.994 549.48) scale(132.506 113.054)"
                >
                  <stop stopColor="#AEC2E7" />
                  <stop offset="1" stopColor="#AEC2E7" stopOpacity="0" />
                </radialGradient>
                <radialGradient
                  id="paint2_radial_1044_1200"
                  cx="0"
                  cy="0"
                  r="1"
                  gradientUnits="userSpaceOnUse"
                  gradientTransform="translate(394.944 712.197) scale(147.699 148.306)"
                >
                  <stop stopColor="#EEB68B" />
                  <stop offset="1" stopColor="#EEB68B" stopOpacity="0" />
                </radialGradient>
              </defs>
            </svg>
          </div>

          <div className="hidden md:block">
            <div
              className="absolute w-[568px] h-[485px] opacity-70 -translate-x-1/2 -translate-y-1/2 rotate-[-147.086deg] blur-[96px] animate-blob blob-gradient-blue-1"
              style={{ left: "97.9%", top: "44.2%" }}
            ></div>

            <div
              className="absolute w-[437px] h-[373px] opacity-64 -translate-x-1/2 -translate-y-1/2 rotate-[-147.086deg] blur-[74px] animate-blob-delayed blob-gradient-blue-2"
              style={{ left: "-1.6%", top: "47.3%" }}
            ></div>

            <div
              className="absolute w-[486px] h-[488px] opacity-87  -translate-x-1/2 -translate-y-1/2 rotate-[-152.879deg] blur-[96px] animate-blob-delayed-2 blob-gradient-orange"
              style={{ left: "65.1%", top: "67.5%" }}
            ></div>
          </div>
        </div>
        <div
          ref={heroTextRef}
          id="hero-text"
          className="relative z-10  pb-20 container px-4 sm:px-6 lg:px-8 sm:p-4  mt-[110px] sm:mt-12 md:mt-10 lg:mt-0 flex flex-col items-center justify-center"
        >
          {data?.tagline && (
            <div className="flex items-center justify-center mb-4">
              <div className="w-5 h-0.5 bg-orange-500 mr-3"></div>
              <p className="text-xs font-semibold text-orange-500 tracking-wider uppercase">
                {data.tagline}
              </p>
            </div>
          )}

          <h1
            ref={headingRef}
            className="text-[30px] max-sm:w-[332px] sm:text-3xl md:text-[40px] lg:text-[56px] xl:text-[62px] font-bold text-white leading-tight max-w-5xl mx-auto :leading-[64px] xl:leading-[76.8px] opacity-0"
          >
            {data?.title &&
              (() => {
                const words = data.title.split(" ");
                const lastWord = words.pop();
                return (
                  <>
                    {words.join(" ")}
                    {words.length > 0 && " "}
                    <span className="text-theme-primary">{lastWord}</span>
                  </>
                );
              })()}
          </h1>
          <p
            ref={subtitleRef}
            className="mt-4 sm:mt-6 text-base sm:text-lg md:text-[18px] text-[#FAFAFACC] max-sm:font-semibold max-w-[970px] mx-auto opacity-0"
          >
            {data?.desc}
          </p>

          <div ref={ctaRef} className="mt-6 sm:mt-10 opacity-0">
            <HeroButtons buttons={data?.buttons} />
          </div>
        </div>

        <div
          ref={deviceMockupRef}
          id="device-mockup"
          className="sm:mt-8 md:mt-10 lg:mt-6 2xl:mt-2 w-full md:flex md:bg-[#c3c6ca1c] justify-center items-center md:w-fit md:p-[16px] md:border border-[#374151] md:rounded-[16px] md:box-shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)]"
        >
          <div
            ref={videoInnerRef}
            className="flex justify-center items-center w-full lg:w-[80vw] max-w-4xl z-0 will-change-transform will-change-scale transform-origin-center-center xl:max-w-[900px]"
          >
            {/* Reserve space to prevent layout shift - use consistent aspect ratio */}
            <div className="bg-gray-100 rounded-lg overflow-hidden relative w-full aspect-video min-h-[220px] md:min-h-[450px] md:w-[750px] md:h-[450px] lg:w-[900px] lg:h-[540px] xl:w-[900px] xl:h-[540px]">
              <VideoPlayer
                imageUrl={data?.video?.poster}
                imageAlt={data?.video?.altText || "Platform Preview"}
                videoSrc={data?.video?.src}
                posterUrl={data?.video?.poster}
                videoIcon={data?.video?.videoIcon}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroClient;
